// Opening & Closing Balance Report — one row per party per month.
// Closing reconciles with the Party Ledger (closing = opening + Σ signed).
// Every amount / count is clickable → slide-in drill panel.

import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import EmptyState from "@/components/EmptyState";
import PageShortcutHeader from "@/components/PageShortcutHeader";
import { withDatePrefix } from "@/lib/downloadFilename";
import { Scales, FilePdf, FileXls, CircleNotch, CalendarBlank } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n || 0));
const fmtFull = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(Number(n || 0));
const fmtSigned = (n) => {
    const v = Number(n || 0);
    if (v === 0) return "₹0";
    return `${fmt(Math.abs(v))} ${v > 0 ? "Dr" : "Cr"}`;
};
const dmY = (d) => {
    if (!d) return "—";
    const [y, m, day] = String(d).slice(0, 10).split("-");
    return day ? `${day}-${m}-${y}` : d;
};
const thisMonth = () => new Date().toISOString().slice(0, 7);
const monthsAgo = (n) => { const d = new Date(); d.setMonth(d.getMonth() - n); return d.toISOString().slice(0, 7); };

export default function OpeningClosingReport() {
    const [parties, setParties] = useState([]);
    const [partyId, setPartyId] = useState("all");
    const [from, setFrom] = useState(monthsAgo(3));
    const [to, setTo] = useState(thisMonth());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [drill, setDrill] = useState(null); // { title, crumb, kind, row, txns }
    const [dlPdf, setDlPdf] = useState(false);
    const [dlXls, setDlXls] = useState(false);

    useEffect(() => {
        api.get("/parties").then((r) => setParties(r.data)).catch((e) => toast.error(formatApiError(e)));
    }, []);

    const run = () => {
        if (from > to) { toast.error("From month is after To month"); return; }
        setLoading(true); setError(""); setData(null);
        api.get("/reports/opening-closing", { params: { date_from: from, date_to: to, party_id: partyId }, timeout: 20000 })
            .then((r) => setData(r.data))
            .catch((e) => {
                const msg = e?.code === "ECONNABORTED" ? "Report too large — please narrow your date range" : formatApiError(e);
                setError(msg); toast.error(msg);
            })
            .finally(() => setLoading(false));
    };

    const doDownload = async (kind) => {
        if (!data) return;
        const setter = kind === "xlsx" ? setDlXls : setDlPdf;
        setter(true);
        try {
            const res = await api.get(`/export/opening-closing.${kind}`, {
                params: { date_from: from, date_to: to, party_id: partyId }, responseType: "blob", timeout: 30000,
            });
            const type = kind === "xlsx"
                ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                : "application/pdf";
            const url = window.URL.createObjectURL(new Blob([res.data], { type }));
            const a = document.createElement("a");
            a.href = url;
            a.download = withDatePrefix(`opening-closing-${from}_to_${to}.${kind}`);
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(formatApiError(e) || `Failed to download ${kind.toUpperCase()}`);
        } finally { setter(false); }
    };

    const rows = data?.rows || [];
    const grand = data?.grand_total || {};

    const crumbFor = (row, n) => `O&C Report → ${row.party_name} → ${row.month_label} → ${n} transactions`;
    const openRecd = (row) => setDrill({ kind: "recd", row, crumb: crumbFor(row, row.recd_count), txns: row.recd_txns || [] });
    const openPaid = (row) => setDrill({ kind: "paid", row, crumb: crumbFor(row, row.paid_count), txns: row.paid_txns || [] });
    const openBreakdown = (row) => setDrill({ kind: "breakdown", row, crumb: crumbFor(row, row.recd_count + row.paid_count), txns: [] });

    return (
        <div className="space-y-3 md:space-y-6" data-testid="oc-report-page">
            <PageShortcutHeader
                action_id="page:opening-closing"
                label="Opening & Closing Balance"
                target_route="/admin/reports/opening-closing"
                eyebrow="Statements"
                title="Opening & Closing Balance Report"
                titleClassName="mt-1 font-display text-lg font-bold tracking-tight text-slate-900 md:text-4xl"
                subtitle="Per-party, month-by-month opening & closing balances with received / paid drill-downs. Closing reconciles with the Party Ledger."
                subtitleClassName="mt-2 hidden text-sm text-slate-500 md:block"
            />

            {/* Filters — single row */}
            <div className="grid grid-cols-2 gap-2 rounded-lg border border-slate-200 bg-white p-2 shadow-sm md:grid-cols-4 md:gap-4 md:p-4" data-testid="oc-filters">
                <div className="col-span-2 min-w-0 md:col-span-1">
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 md:text-xs">Party</Label>
                    <select data-testid="oc-party-select" value={partyId} onChange={(e) => setPartyId(e.target.value)}
                        className="mt-1 h-10 w-full min-w-0 rounded-md border border-slate-300 bg-white px-3 text-sm">
                        <option value="all">ALL PARTIES</option>
                        {parties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                </div>
                <div className="min-w-0">
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 md:text-xs">From (Month/Year)</Label>
                    <input type="month" data-testid="oc-from" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 h-10 w-full min-w-0 max-w-full rounded-md border border-slate-300 px-3 text-sm" />
                </div>
                <div className="min-w-0">
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 md:text-xs">To (Month/Year)</Label>
                    <input type="month" data-testid="oc-to" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 h-10 w-full min-w-0 max-w-full rounded-md border border-slate-300 px-3 text-sm" />
                </div>
                <div className="col-span-2 flex items-end md:col-span-1">
                    <button type="button" data-testid="oc-generate" onClick={run} disabled={loading}
                        className="h-10 w-full rounded-md bg-slate-900 px-4 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60">
                        {loading ? "Loading…" : "Generate Report"}
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-slate-200 bg-white py-16" data-testid="oc-loading">
                    <CircleNotch size={34} weight="bold" className="animate-spin text-emerald-600" />
                    <div className="text-sm font-medium text-slate-600">Generating report… the page stays responsive.</div>
                </div>
            ) : !data ? (
                <EmptyState icon={CalendarBlank} title={error ? "Report failed" : "Generate a report"} description={error ? error : "Pick a party (or ALL PARTIES), a month range, then Generate Report."} />
            ) : (
                <>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="text-sm text-slate-500">Range: <span className="font-semibold text-slate-800">{data.date_from} → {data.date_to}</span> · {data.party_label}</div>
                        <div className="flex gap-2">
                            <button type="button" data-testid="oc-export-pdf" onClick={() => doDownload("pdf")} disabled={dlPdf}
                                className="inline-flex items-center gap-1.5 rounded-md border border-rose-200 bg-rose-50 px-3 py-1.5 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60">
                                <FilePdf size={16} weight="fill" /> {dlPdf ? "Preparing…" : "Export PDF"}
                            </button>
                            <button type="button" data-testid="oc-export-excel" onClick={() => doDownload("xlsx")} disabled={dlXls}
                                className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60">
                                <FileXls size={16} weight="fill" /> {dlXls ? "Preparing…" : "Export Excel"}
                            </button>
                        </div>
                    </div>

                    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="oc-table">
                                <thead>
                                    <tr className="bg-emerald-700 text-left text-[10px] font-semibold uppercase tracking-wider text-white">
                                        <th className="px-4 py-3">Party Name</th>
                                        <th className="px-4 py-3">Month</th>
                                        <th className="px-4 py-3 text-right">Opening Balance</th>
                                        <th className="px-4 py-3 text-right">Total Recd</th>
                                        <th className="px-4 py-3 text-right">Total Paid</th>
                                        <th className="px-4 py-3 text-right">Net</th>
                                        <th className="px-4 py-3 text-right">Closing Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length === 0 && (
                                        <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No activity in this range.</td></tr>
                                    )}
                                    {rows.map((r) => {
                                        const key = `${r.party_id}-${r.month}`;
                                        return (
                                            <tr key={key} data-testid={`oc-row-${key}`} className="border-t border-slate-100 hover:bg-blue-50/40">
                                                <td className="px-4 py-2.5 font-semibold text-slate-800">{r.party_name}</td>
                                                <td className="px-4 py-2.5 text-slate-600">{r.month_label}</td>
                                                <td className="px-4 py-2.5 text-right">
                                                    <button type="button" data-testid={`oc-open-${key}`} onClick={() => openBreakdown(r)} className="tabular-nums font-medium text-blue-700 underline decoration-dotted hover:text-blue-900">{fmtSigned(r.opening)}</button>
                                                </td>
                                                <td className="px-4 py-2.5 text-right">
                                                    <button type="button" data-testid={`oc-recd-${key}`} onClick={() => openRecd(r)} className="tabular-nums text-emerald-700 underline decoration-dotted hover:text-emerald-900">
                                                        {fmt(r.total_recd)} <span className="text-[11px] text-slate-500">({r.recd_count} slips)</span>
                                                    </button>
                                                </td>
                                                <td className="px-4 py-2.5 text-right">
                                                    <button type="button" data-testid={`oc-paid-${key}`} onClick={() => openPaid(r)} className="tabular-nums text-violet-700 underline decoration-dotted hover:text-violet-900">
                                                        {fmt(r.total_paid)} <span className="text-[11px] text-slate-500">({r.paid_count})</span>
                                                    </button>
                                                </td>
                                                <td className="px-4 py-2.5 text-right">
                                                    <button type="button" data-testid={`oc-net-${key}`} onClick={() => openBreakdown(r)} className="tabular-nums font-semibold text-slate-800 underline decoration-dotted hover:text-slate-950">{fmtSigned(r.net)}</button>
                                                </td>
                                                <td className="px-4 py-2.5 text-right">
                                                    <button type="button" data-testid={`oc-close-${key}`} onClick={() => openBreakdown(r)} className="tabular-nums font-semibold text-slate-900 underline decoration-dotted hover:text-black">{fmtSigned(r.closing)}</button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    <tr data-testid="oc-grand-total" className="border-t-2 border-emerald-700 bg-slate-50 font-bold">
                                        <td className="px-4 py-3 uppercase tracking-wider text-[11px] text-slate-700" colSpan={2}>Grand Total · {rows.length} rows</td>
                                        <td className="px-4 py-3 text-right tabular-nums">{fmtSigned(grand.opening)}</td>
                                        <td className="px-4 py-3 text-right tabular-nums text-emerald-700">{fmt(grand.total_recd)} <span className="text-[11px] font-normal text-slate-500">({grand.recd_count})</span></td>
                                        <td className="px-4 py-3 text-right tabular-nums text-violet-700">{fmt(grand.total_paid)} <span className="text-[11px] font-normal text-slate-500">({grand.paid_count})</span></td>
                                        <td className="px-4 py-3 text-right tabular-nums">{fmtSigned(grand.net)}</td>
                                        <td className="px-4 py-3 text-right tabular-nums">{fmtSigned(grand.closing)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <p className="text-center text-xs italic text-slate-500">Click any amount or count to drill into the transactions behind it.</p>
                </>
            )}

            {/* Slide-in drill panel */}
            {drill && (
                <div className="fixed inset-0 z-[60] flex justify-end bg-black/40" data-testid="oc-drill-panel" onClick={() => setDrill(null)}>
                    <div className="flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <div className="shrink-0 border-b border-slate-200 bg-white px-5 py-3">
                            <div className="flex items-center justify-between gap-3">
                                <div className="text-xs text-slate-500" data-testid="oc-drill-breadcrumb">{drill.crumb}</div>
                                <button type="button" data-testid="oc-drill-back" onClick={() => setDrill(null)} className="shrink-0 rounded-md border border-slate-200 px-3 py-1 text-sm font-semibold text-slate-700 hover:bg-slate-100">← Back</button>
                            </div>
                        </div>
                        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-auto p-4" style={{ WebkitOverflowScrolling: "touch" }}>
                            {drill.kind === "breakdown" ? (
                                <div className="space-y-2" data-testid="oc-drill-breakdown">
                                    <div className="text-sm font-semibold text-slate-800">Balance breakdown — {drill.row.month_label}</div>
                                    <table className="w-full text-sm">
                                        <tbody className="tabular-nums">
                                            <BRow label="Opening balance (start of month)" value={fmtSigned(drill.row.opening)} />
                                            <BRow label={`Received (credit · ${drill.row.recd_count} slips)`} value={`− ${fmtFull(drill.row.total_recd)}`} tone="text-emerald-700" />
                                            <BRow label={`Paid (debit · ${drill.row.paid_count})`} value={`+ ${fmtFull(drill.row.total_paid)}`} tone="text-violet-700" />
                                            {Number(drill.row.other) !== 0 && (
                                                <BRow label="Other adjustments (commission / fees / invoices)" value={fmtSigned(drill.row.other)} tone="text-slate-600" />
                                            )}
                                            <tr className="border-t-2 border-slate-800 font-bold">
                                                <td className="py-2 pr-2 text-slate-800">Closing balance</td>
                                                <td className="py-2 text-right text-slate-900">{fmtSigned(drill.row.closing)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <p className="pt-1 text-[11px] italic text-slate-500">Closing = Opening − Received + Paid + other signed movements, matching the Party Ledger (a payment received is a credit that reduces the balance).</p>
                                </div>
                            ) : (
                                <table className="w-full text-xs" data-testid="oc-drill-table">
                                    <thead>
                                        <tr className="bg-slate-800 text-left uppercase tracking-wider text-white">
                                            <th className="px-2 py-2">Date</th>
                                            <th className="px-2 py-2">UTR</th>
                                            <th className="px-2 py-2">Payment Mode</th>
                                            <th className="px-2 py-2 text-right">Amount</th>
                                            {drill.kind === "recd" && <th className="px-2 py-2">Slip Status</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {drill.txns.length === 0 && <tr><td colSpan={drill.kind === "recd" ? 5 : 4} className="px-2 py-6 text-center text-slate-400">No transactions.</td></tr>}
                                        {drill.txns.map((t, i) => (
                                            <tr key={i} data-testid={`oc-drill-txn-${i}`} className="border-t border-slate-100">
                                                <td className="px-2 py-1.5 text-slate-600">{dmY(t.date)}</td>
                                                <td className="px-2 py-1.5 font-mono text-slate-700">{t.utr || "—"}</td>
                                                <td className="px-2 py-1.5 uppercase text-slate-500">{t.mode || "—"}</td>
                                                <td className={`px-2 py-1.5 text-right tabular-nums ${drill.kind === "recd" ? "text-emerald-700" : "text-violet-700"}`}>{fmtFull(t.amount)}</td>
                                                {drill.kind === "recd" && <td className="px-2 py-1.5"><span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-600">{t.status || "—"}</span></td>}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function BRow({ label, value, tone = "text-slate-700" }) {
    return (
        <tr className="border-t border-slate-100">
            <td className="py-1.5 pr-2 text-slate-600">{label}</td>
            <td className={`py-1.5 text-right ${tone}`}>{value}</td>
        </tr>
    );
}
